import React, { useState, useEffect } from "react";
import RopePiece from "../components/RopePiece";
import Scissors from "../components/Scissors";
import InputBox from "../components/InputBox";
import ProgressPopup from "../components/ProgressPopup";
import { COLOR_DIVISOR, FONT, increaseProgress, REPEATS } from "../defaults";
import {
  level0Correct,
  level3Incorrect1,
  level3Incorrect2,
  level3Incorrect3,
  level3Question,
  level3Welcome1,
  level3Welcome2,
  level3Welcome3,
  nextLevelUnlocked,
} from "../strings";

const LevelNumber = 3;

function calculatePieceCounts(currentProgress) {
  const numbers = [4, 5, 3];
  if (currentProgress >= numbers.length) {
    return Math.ceil(Math.random() * 5) + 1;
  }
  return numbers[currentProgress];
}

function calculatePieceSizes(pieceCount, currentProgress) {
  const numbers = [5, 4, 7];
  if (currentProgress >= numbers.length) {
    return Math.ceil(Math.random() * (5 - pieceCount / 2)) + 2;
  }
  return numbers[currentProgress];
}

function Level3({ levelProgress, setLevelProgress }) {
  const [scissorProgress, setScissorProgress] = useState(0);
  const [pieceCount, setPieceCount] = useState(
    calculatePieceCounts(levelProgress[LevelNumber])
  );
  const [pieceSize, setPieceSize] = useState(
    calculatePieceSizes(pieceCount, levelProgress[LevelNumber])
  );

  useEffect(() => {
    const count = calculatePieceCounts(levelProgress[LevelNumber]);
    setPieceCount(count);
    setPieceSize(calculatePieceSizes(count, levelProgress[LevelNumber]));
    setFallingPieces(Array(count).fill(false));
    setScissorProgress(0);
  }, [levelProgress]);

  const [fallingPieces, setFallingPieces] = useState(
    Array(pieceCount).fill(false)
  );

  const handleScissorsClick = (index) => {
    if (scissorProgress === index) {
      const newFallingPieces = [...fallingPieces];
      newFallingPieces[index] = true;
      if (index === pieceCount - 2) {
        newFallingPieces[index + 1] = true;
      }
      setFallingPieces(newFallingPieces);
      setScissorProgress((prev) => prev + 1);
    }
  };

  const getFallHeight = (index) => {
    return index % 2 === 0 ? 16 : 13;
  };

  const numberLineValues = Array.from(
    { length: pieceCount * pieceSize + 1 },
    (_, index) => index
  );

  const [message, setMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const handleCorrect = () => {
    if (levelProgress[LevelNumber] + 1 === REPEATS) {
      setMessage(level0Correct + nextLevelUnlocked);
    } else {
      setMessage(level0Correct);
    }
    setShowModal(true);
    increaseProgress(levelProgress, setLevelProgress, LevelNumber);
  };

  const handleIncorrect = () => {
    setMessage(
      level3Incorrect1 +
        (scissorProgress + 1 === pieceCount ? "" : level3Incorrect2) +
        "\n" +
        pieceCount * pieceSize +
        ":" +
        pieceSize +
        level3Incorrect3 +
        pieceSize +
        "."
    );
    setShowModal(true);
  };

  const hideModal = () => {
    setShowModal(false);
  };

  return (
    <div
      style={{ position: "relative", marginTop: "2dvh", marginBottom: "1dvh" }}
    >
      <div
        className="Welcome-label"
        style={{ fontFamily: FONT, fontSize: "3.5dvh" }}
      >
        {level3Welcome1}
        <span style={{ color: COLOR_DIVISOR }}>{pieceSize}</span>
        {level3Welcome2}
        {pieceCount * pieceSize}?<br></br>
        {level3Welcome3}
      </div>
      <ProgressPopup show={showModal} handleClose={hideModal}>
        {message}
      </ProgressPopup>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "4dvh",
          marginBottom: "1dvh",
        }}
      >
        <div
          style={{
            display: "flex",
            position: "relative",
            alignItems: "start",
            height: "5dvh",
          }}
        >
          {Array.from({ length: pieceCount }).map((_, index) => (
            <React.Fragment key={index}>
              <RopePiece
                pieceSize={pieceSize}
                isFalling={fallingPieces[index]}
                fallHeight={fallingPieces[index] ? getFallHeight(index) : 0}
                showDot={index === scissorProgress && index + 1 < pieceCount}
              />
              {index < pieceCount - 1 && (
                <div style={{ position: "relative", width: `0px` }}>
                  <Scissors
                    onClick={() => handleScissorsClick(index)}
                    visibility={scissorProgress === index ? 1 : 0}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        <div
          className="number-line"
          style={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            width: `${pieceCount * pieceSize * 5 + 5}dvh`,
            height: "6dvh",
            marginTop: "1dvh",
          }}
        >
          <hr
            className="line"
            style={{
              position: "absolute",
              top: "50%",
              width: `${pieceCount * pieceSize * 5}dvh`,
              height: "0.3dvh",
              backgroundColor: "black",
              border: "0.15dvh solid black",
            }}
          ></hr>
          <div style={{ display: "flex" }}>
            {numberLineValues.map((value, index) => (
              <div
                key={index}
                className="tick"
                style={{
                  position: "relative",
                  height: "100%",
                  width: "5dvh",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  className="number"
                  style={{
                    fontFamily: FONT,
                    fontSize: "3dvh",
                    textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
                  }}
                >
                  {value}
                </div>
                <div
                  key={index}
                  style={{
                    position: "relative",
                    width: "2px",
                    height: "15px",
                    backgroundColor: "black",
                  }}
                ></div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {scissorProgress + 1 === pieceCount && (
        <div style={{ marginTop: "10dvh" }}>
          <span
            className="text-wrapper"
            style={{
              fontFamily: FONT,
              color: "white",
              fontSize: "3dvh",
              textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
            }}
          >
            {level3Question}
          </span>
          <div style={{ height: "1dvh" }}></div>
          <InputBox
            handleCorrect={handleCorrect}
            handleIncorrect={handleIncorrect}
            dividend={pieceCount * pieceSize}
            divisor={pieceSize}
          >
            {" "}
          </InputBox>
        </div>
      )}
    </div>
  );
}

export default Level3;
